
import { Dialog } from "@/components/ui/dialog";
import { PolicyDialogContent } from "./dialog/PolicyDialogContent";
import { usePolicyDialog } from "../hooks/usePolicyDialog";
import { LeadSourceType } from "@/types/leadSource";
import { useProfileData } from "@/hooks/useProfileData";
import { useState } from "react";

interface PolicyCreationDialogProps {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPolicyCreated: () => void;
}

export const PolicyCreationDialog = ({ 
  userId, 
  open, 
  onOpenChange,
  onPolicyCreated 
}: PolicyCreationDialogProps) => {
  const { profile } = useProfileData(userId);
  const clientName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    formState,
    handlers,
    handleSubmit,
    isFormValid
  } = usePolicyDialog({
    userId,
    onPolicyCreated: () => {
      onPolicyCreated();
      setIsSubmitting(false);
      onOpenChange(false);
    },
    onOpenChange,
  });

  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    try {
      await handleSubmit();
    } catch (error) {
      setIsSubmitting(false);
      // Error is already handled in usePolicyDialog
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(newOpen) => {
        if (!isSubmitting) {
          onOpenChange(newOpen);
        }
      }}
    >
      <PolicyDialogContent
        userId={userId}
        isSubmitting={isSubmitting}
        producer={formState.producer}
        policyType={formState.policyType}
        insuranceCarrier={formState.insuranceCarrier}
        policyLine={formState.policyLine}
        policyNumber={formState.policyNumber}
        startDate={formState.startDate}
        endDate={formState.endDate}
        premiumAmount={formState.premiumAmount}
        status={formState.status}
        leadSource={formState.leadSource}
        policyAcquisitionType={formState.policyAcquisitionType}
        onProducerChange={handlers.setProducer}
        onPolicyTypeChange={handlers.setPolicyType}
        onInsuranceCarrierChange={handlers.setInsuranceCarrier}
        onPolicyLineChange={handlers.setPolicyLine}
        onPolicyNumberChange={(e) => handlers.setPolicyNumber(e.target.value)}
        onStartDateChange={handlers.setStartDate}
        onEndDateChange={handlers.setEndDate}
        onPremiumAmountChange={handlers.setPremiumAmount}
        onStatusChange={handlers.setStatus}
        onLeadSourceChange={(value: LeadSourceType) => handlers.setLeadSource(value)}
        onPolicyAcquisitionTypeChange={handlers.setPolicyAcquisitionType}
        onSalesDateChange={handlers.setSalesDate}
        onSubmit={handleFormSubmit}
        onCancel={() => !isSubmitting && onOpenChange(false)}
        clientName={clientName}
        isFormValid={Boolean(isFormValid)}
      />
    </Dialog>
  );
};
